// Canal « Collagène : orientation » : organisation des fibres de collagène à partir du SHG, par le tenseur de
// structure (méthode d'OrientationJ), calculé une fois sur un niveau réduit de la pyramide :
// - SHG ramené à [0, 1] (du fond, médiane sous le seuil d'Otsu, au 99,5e centile du signal), lissé (σ 1 px) ;
// - gradients (différences centrées), tenseur J = [gx², gx·gy ; gx·gy, gy²] lissé à l'échelle d'analyse σ ;
// - orientation des fibres = perpendiculaire au gradient dominant, ½ atan2(2Jxy, Jxx − Jyy) + 90° dans le repère de
//   l'image (y vers le bas), exprimée comme sur un rapporteur à l'écran : α dans [0, 180[, 0° horizontale, 90°
//   verticale, angles comptés vers le haut (sens trigonométrique) ; cohérence = √((Jxx − Jyy)² + 4Jxy²) / (Jxx + Jyy), de 0 (fibres en
//   tous sens) à 1 (fibres parallèles) ;
// - collagène = SHG lissé à σ au-dessus du seuil (Otsu par défaut) ; hors du collagène, rien.
// Affichage : image semi-transparente au-dessus du SHG en pleine résolution, en orientation (teinte = 2α : les
// directions opposées ont la même couleur ; saturation = cohérence) ou en alignement (cohérence par une LUT). Chiffres :
// cohérence moyenne, orientation dominante (moyenne circulaire de 2α, pondérée par la cohérence) et dispersion.
// Vecteurs (en option, au-dessus) : un trait sans flèche par bloc de 2^k px d'analyse, k suivant le zoom ; le tenseur
// est moyenné sur le collagène du bloc (somme du vecteur d'angle double), d'où l'angle et la cohérence du bloc.
import { gaussBlur } from './blur';
import { otsu } from './blood';
import { lutColor } from './lut';
import { pixelSize } from '../../services';

export const COLLAGEN_ID = 'collagen';  // identifiant du canal parmi les modalités
// detail : niveau d'analyse, le plus fin d'au plus 5 (standard) ou 15 (fin) millions de pixels ; sigma : échelle
// d'analyse en px de ce niveau ; threshold : seuil du collagène dans [0, 1] (null : Otsu) ; display 'none' : vecteurs
// seuls. vectors : traits par-dessus, couleur (white|orientation), longueur (coherence|fixed), épaisseur en px d'écran.
export const COLLAGEN_DEFAULTS = {
  c: null, display: 'orientation', detail: 'standard', sigma: 3, threshold: null, opacity: 0.85, lut: 'fire', lutRange: null,
  vectors: false, vecColor: 'white', vecLength: 'coherence', vecWidth: 1.5,
};
const MAX_PIXELS = { standard: 5e6, fin: 15e6 };
const VECTOR_SPACING = 28;  // écart visé entre deux traits, en px d'écran
const MAX_VECTORS = 3e5;    // blocs les plus fins : au plus autant de traits sur l'image entière
const MIN_FILL = 0.1;       // part de collagène d'un bloc pour y tracer un trait

// Niveau le plus fin d'au plus maxPixels pixels.
function analysisLevel(loader, maxPixels) {
  let level = 0;
  while (level < loader.length - 1) {
    const { width, height } = pixelSize(loader[level]);
    if (width * height <= maxPixels) break;
    level += 1;
  }
  return level;
}

// SHG au niveau d'analyse, ramené à [0, 1] : { width, height, cell (px pleine résolution par px), shg, otsu (seuil
// dans [0, 1]) }.
export async function collagenData(loader, detail) {
  const level = analysisLevel(loader, MAX_PIXELS[detail] || MAX_PIXELS.standard);
  const { data, width, height } = await loader[level].getRaster({ selection: { t: 0, z: 0, c: 0 } });
  const t = otsu(data);
  const below = Float64Array.from(data.filter(v => v <= t)).sort();
  const above = Float64Array.from(data.filter(v => v > t)).sort();
  const low = below.length ? below[Math.floor(below.length / 2)] : 0;
  const high = Math.max(low + 1, above.length ? above[Math.floor(above.length * 0.995)] : t);
  const shg = new Float32Array(data.length);
  for (let i = 0; i < data.length; i += 1) shg[i] = Math.min(1, Math.max(0, (data[i] - low) / (high - low)));
  const { width: full } = pixelSize(loader[0]);
  return { width, height, cell: full / width, shg, otsu: Math.min(1, Math.max(0, (t - low) / (high - low))) };
}

// Tenseur de structure à l'échelle sigma : { theta (orientation des fibres, degrés dans [0, 180[), coherence, energy
// (SHG lissé à sigma), trace (Jxx + Jyy, pour moyenner le tenseur par blocs) }.
export function collagenField({ width: w, height: h, shg }, { sigma }) {
  const n = w * h;
  const s = gaussBlur(shg.slice(), w, h, 1);
  const jxx = new Float32Array(n);
  const jyy = new Float32Array(n);
  const jxy = new Float32Array(n);
  for (let y = 0; y < h; y += 1) {
    const up = (y > 0 ? y - 1 : y) * w;
    const down = (y < h - 1 ? y + 1 : y) * w;
    for (let x = 0; x < w; x += 1) {
      const i = y * w + x;
      const gx = (s[y * w + (x < w - 1 ? x + 1 : x)] - s[y * w + (x > 0 ? x - 1 : x)]) / 2;
      const gy = (s[down + x] - s[up + x]) / 2;
      jxx[i] = gx * gx; jyy[i] = gy * gy; jxy[i] = gx * gy;
    }
  }
  const bxx = gaussBlur(jxx, w, h, sigma);
  const byy = gaussBlur(jyy, w, h, sigma);
  const bxy = gaussBlur(jxy, w, h, sigma);
  const energy = gaussBlur(shg.slice(), w, h, sigma);
  const theta = new Float32Array(n);
  const coherence = new Float32Array(n);
  const trace = bxx;  // réutilisé : Jxx n'est plus lu une fois la trace calculée
  for (let i = 0; i < n; i += 1) {
    const d = bxx[i] - byy[i];
    const t = bxx[i] + byy[i];
    coherence[i] = t > 1e-12 ? Math.sqrt(d * d + 4 * bxy[i] * bxy[i]) / t : 0;
    const gradient = 0.5 * Math.atan2(2 * bxy[i], d) * (180 / Math.PI);  // gradient dominant, repère de l'image
    theta[i] = (((90 - gradient) % 180) + 180) % 180;  // fibres (perpendiculaires), angle compté vers le haut à l'écran
    trace[i] = t;
  }
  return { theta, coherence, energy, trace };
}

// Tenseur moyenné par blocs, sur le collagène (énergie > seuil) : pyramide de blocs de 2^k px d'analyse, k de k0 (au
// plus MAX_VECTORS blocs) au bloc unique. Par bloc, sommes du vecteur d'angle double u = cohérence × trace × (cos 2α,
// sin 2α) (partie anisotrope du tenseur), de la trace et du nombre de px de collagène : angle du bloc ½ atan2(Σuy, Σux),
// cohérence |Σu| / Σtrace.
export function collagenBlocks({ width: w, height: h }, { theta, coherence, trace, energy }, threshold) {
  const k0 = Math.max(1, Math.ceil(Math.log2(Math.sqrt((w * h) / MAX_VECTORS))));
  let lw = Math.ceil(w / 2 ** k0);
  let lh = Math.ceil(h / 2 ** k0);
  let level = { k: k0, w: lw, h: lh, ux: new Float32Array(lw * lh), uy: new Float32Array(lw * lh), t: new Float32Array(lw * lh), n: new Float32Array(lw * lh) };
  for (let y = 0; y < h; y += 1) {
    const row = (y >> k0) * lw;
    for (let x = 0; x < w; x += 1) {
      const i = y * w + x;
      if (!(energy[i] > threshold)) continue;
      const b = row + (x >> k0);
      const a = (2 * theta[i] * Math.PI) / 180;
      const m = coherence[i] * trace[i];
      level.ux[b] += m * Math.cos(a); level.uy[b] += m * Math.sin(a); level.t[b] += trace[i]; level.n[b] += 1;
    }
  }
  const levels = [level];
  while (lw > 1 || lh > 1) {
    const prev = level;
    lw = Math.ceil(prev.w / 2);
    lh = Math.ceil(prev.h / 2);
    level = { k: prev.k + 1, w: lw, h: lh, ux: new Float32Array(lw * lh), uy: new Float32Array(lw * lh), t: new Float32Array(lw * lh), n: new Float32Array(lw * lh) };
    for (let y = 0; y < prev.h; y += 1) {
      for (let x = 0; x < prev.w; x += 1) {
        const i = y * prev.w + x;
        const b = (y >> 1) * lw + (x >> 1);
        level.ux[b] += prev.ux[i]; level.uy[b] += prev.uy[i]; level.t[b] += prev.t[i]; level.n[b] += prev.n[i];
      }
    }
    levels.push(level);
  }
  return { width: w, height: h, levels };
}

// Niveau de blocs pour un zoom deck.gl : un trait tous les VECTOR_SPACING px d'écran (cell : px pleine résolution par
// px d'analyse), dans les niveaux calculés.
export function vectorLevel({ levels }, cell, zoom) {
  const k = Math.round(Math.log2(VECTOR_SPACING / (cell * 2 ** zoom)));
  return Math.min(levels[levels.length - 1].k, Math.max(levels[0].k, k));
}

// Traits d'un niveau de blocs, en px pleine résolution : { length, ends (Float32Array, x0 y0 x1 y1 par trait), colors
// (Uint8Array RGBA, couleur de l'orientation) }. Blocs d'au moins MIN_FILL de collagène ; demi-longueur 0,45 bloc,
// × cohérence si vecLength vaut 'coherence'.
export function collagenVectors({ width: w, height: h, levels }, k, cell, { vecLength }) {
  const level = levels.find(l => l.k === k) || levels[0];
  const size = 2 ** level.k;
  const ends = new Float32Array(level.w * level.h * 4);
  const colors = new Uint8Array(level.w * level.h * 4);
  let count = 0;
  for (let by = 0; by < level.h; by += 1) {
    for (let bx = 0; bx < level.w; bx += 1) {
      const b = by * level.w + bx;
      const area = Math.min(size, w - bx * size) * Math.min(size, h - by * size);
      if (level.n[b] < MIN_FILL * area || !(level.t[b] > 0)) continue;
      const coh = Math.min(1, Math.hypot(level.ux[b], level.uy[b]) / level.t[b]);
      const alpha = Math.atan2(level.uy[b], level.ux[b]) / 2;  // radians, compté vers le haut à l'écran
      const half = 0.45 * size * cell * (vecLength === 'fixed' ? 1 : coh);
      const cx = (bx + 0.5) * size * cell;
      const cy = (by + 0.5) * size * cell;
      const [dx, dy] = [half * Math.cos(alpha), -half * Math.sin(alpha)];  // y vers le bas dans l'image
      ends.set([cx - dx, cy - dy, cx + dx, cy + dy], 4 * count);
      const deg = ((((alpha * 180) / Math.PI) % 180) + 180) % 180;
      colors.set([...hsv(2 * deg, 1, 1), 255], 4 * count);
      count += 1;
    }
  }
  return { length: count, ends: ends.subarray(0, 4 * count), colors: colors.subarray(0, 4 * count) };
}

// Couleur RGB (0–255) d'une teinte h (degrés), saturation s et valeur v.
function hsv(h, s, v) {
  const f = k => {
    const kk = (k + h / 60) % 6;
    return v - v * s * Math.max(0, Math.min(kk, 4 - kk, 1));
  };
  return [Math.round(255 * f(5)), Math.round(255 * f(3)), Math.round(255 * f(1))];
}

// Image de la carte (canevas) : collagène (énergie > seuil) en orientation (teinte = 2α, saturation = cohérence) ou en
// alignement (cohérence par la LUT), opacité `opacity` ; transparent ailleurs.
export function collagenCanvas({ width: w, height: h }, field, { display, threshold, opacity, lut, lutRange }) {
  const { theta, coherence, energy } = field;
  const rgba = new Uint8ClampedArray(w * h * 4);
  const colors = Array.from({ length: 256 }, (_, i) => lutColor(lut, [255, 255, 255], false, i / 255, lutRange));
  const alpha = Math.round(255 * opacity);
  for (let i = 0; i < w * h; i += 1) {
    if (!(energy[i] > threshold)) continue;
    const c = display === 'coherence'
      ? colors[Math.min(255, Math.round(coherence[i] * 255))]
      : hsv(2 * theta[i], Math.min(1, coherence[i] * 1.5), 1);
    rgba[4 * i] = c[0]; rgba[4 * i + 1] = c[1]; rgba[4 * i + 2] = c[2]; rgba[4 * i + 3] = alpha;
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').putImageData(new ImageData(rgba, w, h), 0, 0);
  return canvas;
}

// Chiffres sur le collagène (énergie > seuil) : part de l'image, cohérence moyenne, orientation dominante (moyenne
// circulaire de 2α pondérée par la cohérence, ramenée à [0, 180[) et dispersion (1 − longueur du vecteur moyen).
export function collagenStats(field, threshold) {
  const { theta, coherence, energy } = field;
  let n = 0;
  let sumC = 0;
  let sx = 0;
  let sy = 0;
  for (let i = 0; i < energy.length; i += 1) {
    if (!(energy[i] > threshold)) continue;
    n += 1;
    sumC += coherence[i];
    const a = (2 * theta[i] * Math.PI) / 180;
    sx += coherence[i] * Math.cos(a);
    sy += coherence[i] * Math.sin(a);
  }
  if (!n) return null;
  const angle = ((((Math.atan2(sy, sx) * 180) / Math.PI) / 2) + 180) % 180;
  return {
    area: n / energy.length, coherence: sumC / n, angle, dispersion: 1 - Math.hypot(sx, sy) / (sumC || 1),
  };
}

export { hsv as orientationColor };
